/**
 * @chaos/types/effort — the ONE place that decides which effort level a CLI
 * really runs, shared by the gateway (what it sends) and the UI (what the
 * pickers show). Founder 2026-10-04 (verbatim quote in docs/canon/
 * FOUNDER_DECISIONS_INDEX.md): each vendor's picker carries that vendor's own
 * effort names and levels, consistently.
 *
 * WHY IT IS SHARED (review of 2026-10-04, issues 1 + 2). The first cut decided
 * the shown level in the UI and the sent level in the gateway with two
 * different rules: a GPT card with nothing stored SHOWED the model's default
 * (Low on GPT-6.1 Sol) while every gateway path RAN the system default
 * (xhigh); a GPT-5.5 card stored as Max SHOWED Medium while the container
 * launch SENT max. One function, imported by both sides, removes the class:
 *
 *   resolveRunEffort(vendor, table, stored)
 *     1. the stored level, when this model has it;
 *     2. Claude only: the CLI's own downgrade of an unsupported max/xhigh to
 *        high (claude 2.1.287 `if(r==="max"&&!oQ(n))r="high"`);
 *     3. the system default for the vendor (SYSTEM_DEFAULT_EFFORT — the
 *        founder's «default = absolute MAX tier» rule, gateway
 *        TOP_TIER_BY_VENDOR), when this model has it — also what a stored
 *        value this vendor does not have at all falls back to (a GPT card
 *        holding Claude's `ultracode`, a GPT-5.5 card holding `max`);
 *     4. the model's own default level (vendor data), or null = no flag, the
 *        CLI's own default.
 *
 * Claude's Ultracode is NOT a level here: since claude 2.1.287 it is its own
 * toggle ("Changed Ultracode into its own toggle in /effort … it no longer
 * forces xhigh effort and stays on at any effort level"; the settings key is
 * "standing dynamic-workflow orchestration at any effort level"). CHAOS still
 * stores it in the effort column as `ultracode`; with it on, no --effort is
 * passed, so the CLI runs the model's default level
 * (ANTHROPIC_MODEL_DEFAULT_EFFORT) — which is what the pickers show.
 *
 * No imports, so the gateway (Node ESM) and the UI (Vite) load it as is.
 */

export type EffortVendor = 'anthropic' | 'openai' | 'google';

/**
 * System-wide default effort per vendor — the level a card with nothing
 * stored runs. Founder 2026-05-25: «default = absolute MAX tier we have access
 * to, per vendor» (Claude `max`). Founder 2026-10-04 16:41Z: «у GPT нет Max.
 * перепроверь. По умолчанию ставь Extra High» — GPT is `xhigh`, even though
 * codex 0.160.0 also lists Max and Ultra (a separate "More reasoning" group,
 * see OPENAI_ADVANCED_EFFORTS): the 05-25 rule is not read as "highest level
 * the CLI lists". The gateway's TOP_TIER_BY_VENDOR (lib/model-defaults.ts) takes
 * its effort from here; provider_tiers swaps only the model, never the effort
 * (spawn-model-resolver.ts). google is never sent (gemini has no effort flag).
 */
export const SYSTEM_DEFAULT_EFFORT: Readonly<Record<EffortVendor, string>> = {
  anthropic: 'max',
  openai: 'xhigh',
  google: 'high',
};

/**
 * GPT's "More reasoning" levels — the ones codex 0.160.0's own TUI keeps out of
 * the main "Select Reasoning Level" list (Low · Medium · High · Extra high) and
 * puts behind "More reasoning… — Max and Ultra consume usage limits faster"
 * ("Advanced Reasoning", with the warning "Consumes usage limits faster").
 *
 * FOUNDER, 2026-10-04 16:41Z (verbatim): «у GPT нет Max. перепроверь. По
 * умолчанию ставь Extra High» — the GPT default is Extra high (`xhigh`,
 * SYSTEM_DEFAULT_EFFORT.openai above). Max and Ultra are offered only as an
 * explicit, separate choice, and nothing in CHAOS may pick either for GPT on
 * its own: not a fallback, not a role/vendor switch carrying Claude's `max`
 * over, not a missing value.
 */
export const OPENAI_ADVANCED_EFFORTS: readonly string[] = ['max', 'ultra'];

/** True for a GPT level that lives in the "More reasoning" group. */
export function isOpenaiAdvancedEffort(value: string | null | undefined): boolean {
  return typeof value === 'string' && OPENAI_ADVANCED_EFFORTS.includes(value.trim().toLowerCase());
}

/** The system default effort for any vendor string; null for one CHAOS does
 *  not run. Never invents a level (the caller decides what null means). */
export function systemDefaultEffortFor(vendor: string | null | undefined): string | null {
  return vendor === 'anthropic' || vendor === 'openai' || vendor === 'google'
    ? SYSTEM_DEFAULT_EFFORT[vendor]
    : null;
}

/** The stored value that means "Claude Code's Ultracode toggle is on". */
export const ULTRACODE = 'ultracode';

/** A model's effort levels (lowest first) and its own default level. */
export interface ModelEffortTable {
  levels: readonly string[];
  /** The vendor's default level for this model; null = not known. */
  defaultEffort: string | null;
}

/** Lowercase, no `[1m]` variant marker, no dated snapshot suffix. */
export function bareModelId(id: string): string {
  return id.toLowerCase().replace(/\[[^\]]*\]$/, '').replace(/-20\d{6}$/, '');
}

// ── Claude (Anthropic) — claude 2.1.287 ────────────────────────────────────

/** `claude --effort` values, lowest first (bundle validator: "effort must be
 *  one of low, medium, high, xhigh, max"). */
export const ANTHROPIC_EFFORT_LEVELS: readonly string[] = ['low', 'medium', 'high', 'xhigh', 'max'];

/** Models whose bundle entry has no `max` (claude 2.1.287 `oQ`). */
const ANTHROPIC_NO_MAX = /^(claude-3-|claude-opus-4-[015]$|claude-sonnet-4-[05]$|claude-haiku-4-5$)/;
/** Models whose bundle entry has no `xhigh` (no `xhigh_effort` capability). */
const ANTHROPIC_NO_XHIGH = /^(claude-3-|claude-opus-4-[0156]$|claude-sonnet-4-[056]$|claude-haiku-4-5$)/;

/**
 * `default_effort` per model, verbatim from the claude 2.1.287 bundle's model
 * table (measured 2026-10-04 on chaos/agent:latest sha256:2950c618…). A model
 * with effort but no entry runs `high` (the CLI's own fallback,
 * `BS(e,n)??"high"`). Aliases (`opus`, `default`, …) are not resolved here —
 * the account decides them — so they have no known default.
 */
export const ANTHROPIC_MODEL_DEFAULT_EFFORT: Readonly<Record<string, string>> = {
  'claude-sonnet-4-6': 'high',
  'claude-sonnet-5': 'high',
  'claude-sonnet-5-5': 'medium',
  'claude-opus-4-6': 'high',
  'claude-opus-4-7': 'xhigh',
  'claude-opus-4-8': 'high',
  'claude-opus-5': 'high',
  'claude-opus-5-5': 'medium',
  'claude-fable-5': 'high',
  'claude-fable-5-1': 'high',
  'claude-mythos-5-1': 'high',
};

/** Claude's levels for a model id (null/alias = the full ladder). */
export function anthropicEffortTable(modelId: string | null | undefined): ModelEffortTable {
  const id = modelId ? bareModelId(modelId) : '';
  return {
    levels: ANTHROPIC_EFFORT_LEVELS
      .filter((v) => !(v === 'max' && ANTHROPIC_NO_MAX.test(id)))
      .filter((v) => !(v === 'xhigh' && ANTHROPIC_NO_XHIGH.test(id))),
    defaultEffort: ANTHROPIC_MODEL_DEFAULT_EFFORT[id] ?? null,
  };
}

// ── GPT (OpenAI) — codex 0.160.0 ───────────────────────────────────────────

const L4 = ['low', 'medium', 'high', 'xhigh'] as const;
const L5 = [...L4, 'max'] as const;
const L6 = [...L5, 'ultra'] as const;

/** The four levels every GPT model on record supports — the table for a model
 *  nobody has described yet (never a level the model may not have). */
export const OPENAI_COMMON_LEVELS: readonly string[] = L4;

/**
 * FALLBACK ONLY — used when no live per-model list is at hand (the catalogue
 * is still loading, discovery failed, the gateway just restarted). The codex
 * 0.160.0 bundled model catalogue, exactly; the live app-server `model/list`
 * agreed row for row on 2026-10-04.
 */
export const OPENAI_BUNDLED_EFFORTS: Readonly<Record<string, { levels: readonly string[]; default: string }>> = {
  'gpt-6.1-sol': { levels: L6, default: 'low' },
  'gpt-6-astra': { levels: L6, default: 'low' },
  'gpt-6-sol': { levels: L6, default: 'medium' },
  'gpt-6-luna': { levels: L5, default: 'medium' },
  'gpt-5.6-sol': { levels: L6, default: 'low' },
  'gpt-5.6-terra': { levels: L6, default: 'medium' },
  'gpt-5.6-luna': { levels: L5, default: 'medium' },
  'gpt-5.5': { levels: L4, default: 'medium' },
};

/** codex 0.160.0 `isDefault` model — what CHAOS `default` (no `-m`) runs when
 *  no live catalogue has said otherwise. */
export const OPENAI_BUNDLED_DEFAULT_MODEL = 'gpt-6.1-sol';

/** The bundled table for a GPT model id, or the common four levels. */
export function openaiBundledEffortTable(modelId: string | null | undefined): ModelEffortTable {
  const id = !modelId || modelId === 'default' ? OPENAI_BUNDLED_DEFAULT_MODEL : bareModelId(modelId);
  const b = OPENAI_BUNDLED_EFFORTS[id];
  return b ? { levels: b.levels, defaultEffort: b.default } : { levels: OPENAI_COMMON_LEVELS, defaultEffort: null };
}

// ── the decision ───────────────────────────────────────────────────────────

/**
 * The exact effort value the CLI runs for a stored value — the gateway sends
 * it, the UI shows it. null = no effort argument (the CLI's own default for
 * the model; with Claude's Ultracode on, also null — Ultracode travels as its
 * own settings key). `fallback` is the system default for the vendor unless a
 * caller has a better one.
 */
export function resolveRunEffort(
  vendor: EffortVendor,
  table: ModelEffortTable,
  stored: string | null | undefined,
  fallback: string | null = SYSTEM_DEFAULT_EFFORT[vendor],
): string | null {
  if (vendor === 'google' || table.levels.length === 0) return null;
  const v = typeof stored === 'string' ? stored.trim().toLowerCase() : '';
  if (vendor === 'anthropic' && v === ULTRACODE) return null;
  const has = (x: string | null | undefined): x is string => !!x && table.levels.includes(x);
  if (has(v)) return v;
  if (vendor === 'anthropic' && (v === 'max' || v === 'xhigh') && has('high')) return 'high';
  if (has(fallback)) return fallback;
  if (vendor === 'anthropic' && (fallback === 'max' || fallback === 'xhigh') && has('high')) return 'high';
  return has(table.defaultEffort) ? table.defaultEffort : null;
}

/** True when the stored value turns Claude Code's Ultracode toggle on. */
export function isUltracodeOn(vendor: EffortVendor | null | undefined, stored: string | null | undefined): boolean {
  return vendor === 'anthropic' && typeof stored === 'string' && stored.trim().toLowerCase() === ULTRACODE;
}

/**
 * The level the CLI runs while Ultracode is on: no --effort is passed, so it is
 * the model's own default (null = not known, e.g. an alias id).
 */
export function ultracodeRunLevel(table: ModelEffortTable): string | null {
  if (table.levels.length === 0) return null;
  if (table.defaultEffort && table.levels.includes(table.defaultEffort)) return table.defaultEffort;
  return null;
}
